import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  getPlan,
  savePlan,
  getUserPortfolio,
  getFundFamiliesFromCache,
  updateUserPortfolio,
  getFundFamiliesFromWordpressByType,
  getPlanningConfigFromWordpress,
  getUserFiles,
} from '../../../../shared/services/apiGateway';
import selectLatestPPR from '../helpers/selectLatestPPR';
import normalizePlan from '../helpers/normalizePlan';

export const PlanContext = createContext();

// Helper function to create a deep copy
const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));
const copyPlan = (plan) => normalizePlan(deepCopy(plan));

const createEmptyPortfolio = (authenticatedUser) => {
  if (!authenticatedUser?.id) {
    throw new Error('Authenticated user is missing an integration ID');
  }

  return {
    integrationId: String(authenticatedUser.id),
    accounts: [],
    transactions: [],
    ...(authenticatedUser.tier ? { tier: authenticatedUser.tier } : {}),
  };
};

export const PlanProvider = ({ children }) => {
  const authenticatedUser = useSelector((state) => state.auth?.user);
  const [plan, setPlan] = useState({});
  const [updatedPlan, setUpdatedPlan] = useState(null);
  const [userPortfolio, setUserPortfolio] = useState({});
  const [fundFamilies, setFundFamilies] = useState([]);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wordpressCoreFundData, setWordpressCoreFundData] = useState({});
  const [wordpressFocusedFundData, setWordpressFocusedFundData] = useState({});
  const [planningConfig, setPlanningConfig] = useState({});
  const [userPPR, setUserPPR] = useState(null);
  const [planningButtonLoading, setPlanningButtonLoading] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchFundFamilies = async () => {
      try {
        const fundFamiliesResponse = await getFundFamiliesFromWordpressByType(
          '',
          abortController.signal,
          true,
        );
        if (abortController.signal.aborted) {
          return;
        }

        if (fundFamiliesResponse === 'aborted' || !fundFamiliesResponse?.data) {
          throw new Error('Fund family response did not contain data');
        }

        setWordpressCoreFundData(fundFamiliesResponse.data.core);
        setWordpressFocusedFundData(fundFamiliesResponse.data.focused);
      } catch (error) {
        if (!abortController.signal.aborted && error.name !== 'AbortError') {
          console.error('Failed to fetch fund families:', error);
          setIsError(true);
        }
      }
    };

    fetchFundFamilies();
    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchPlanningConfig = async () => {
      try {
        const planningResponse = await getPlanningConfigFromWordpress(
          abortController.signal,
          true,
        );

        if (abortController.signal.aborted) {
          return;
        }

        if (planningResponse === 'aborted' || !planningResponse?.data) {
          throw new Error('Planning configuration response did not contain data');
        }

        setPlanningConfig(planningResponse.data);
      } catch (error) {
        if (!abortController.signal.aborted && error.name !== 'AbortError') {
          console.error('Failed to fetch planning data:', error);
          setIsError(true);
        }
      }
    };

    fetchPlanningConfig();
    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    let ignore = false;

    if (!authenticatedUser?.id) {
      return () => abortController.abort();
    }

    const fetchUserPortfolio = async () => {
      try {
        const portfolio = await getUserPortfolio(
          true,
          true,
          true,
          abortController.signal,
        );
        if (ignore || abortController.signal.aborted) {
          return;
        }

        if (portfolio === 'aborted' || !portfolio?.data) {
          throw new Error('Portfolio response did not contain data');
        }

        setUserPortfolio(portfolio.data);
      } catch (error) {
        if (!ignore && error.name !== 'AbortError') {
          if (error.statusCode === 404) {
            setUserPortfolio(createEmptyPortfolio(authenticatedUser));
          } else {
            console.error('Failed to fetch portfolio:', error);
            setIsError(true);
          }
        }
      }
    };

    fetchUserPortfolio();

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, [authenticatedUser?.id]);

  useEffect(() => {
    const abortController = new AbortController();
    let ignore = false;

    const fetchFundFamilies = async () => {
      try {
        const fundFamilyResponse = await getFundFamiliesFromCache(
          abortController.signal,
          true,
        );
        if (ignore || abortController.signal.aborted) {
          return;
        }

        if (fundFamilyResponse === 'aborted' || !fundFamilyResponse?.data) {
          throw new Error('Fund family cache response did not contain data');
        }

        setFundFamilies(fundFamilyResponse.data);
      } catch (error) {
        if (!ignore && !abortController.signal.aborted) {
          console.error('Failed to fetch cached fund families:', error);
          setIsError(true);
        }
      }
    };

    fetchFundFamilies();

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    let ignore = false;

    const fetchPlan = async () => {
      try {
        const result = await getPlan(
          `?userId=${userPortfolio.integrationId}`,
          abortController.signal,
        );
        if (ignore || abortController.signal.aborted) {
          return;
        }

        if (result === 'aborted' || !result?.data) {
          throw new Error('Plan response did not contain data');
        }

        if (result.data.statusCode !== 404) {
          setPlan(copyPlan(result.data));
          setUpdatedPlan(copyPlan(result.data));
        }

        setLoading(false);
      } catch (error) {
        if (!ignore && !abortController.signal.aborted) {
          console.error('Failed to fetch plan:', error);
          setIsError(true);
          setLoading(false);
        }
      }
    };

    if (userPortfolio && userPortfolio.integrationId) {
      fetchPlan();
    }

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, [userPortfolio]);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchUserPPR = async () => {
      try {
        const pprResponse = await getUserFiles(
          '',
          'Personal_Portfolio_Report',
          abortController.signal,
        );

        if (pprResponse !== 'aborted' && pprResponse && pprResponse.length) {
          setUserPPR(selectLatestPPR(pprResponse));
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Failed to fetch user's PPR:", error);
        }
      }
    };

    fetchUserPPR();
    return () => {
      abortController.abort();
    };
  }, []);

  const handleSavePlan = async (newUpdatedPlan) => {
    try {
      const response = await savePlan(newUpdatedPlan);
      if (response === 'aborted' || !response?.data) {
        throw new Error('Plan save did not return plan data');
      }

      setPlan(copyPlan(response.data));
      setUpdatedPlan(copyPlan(response.data));
      if (newUpdatedPlan.createNewPlan) {
        try {
          const portfolio = await getUserPortfolio(true, true, true);
          if (portfolio !== 'aborted' && portfolio.data) {
            setUserPortfolio(portfolio.data);
          }
        } catch (error) {
          console.error('Failed to refresh portfolio after saving plan:', error);
        }
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      throw new Error('Failed to save plan');
    }
  };

  const handleSaveUserPortfolio = async (updatedUserPotfolio) => {
    try {
      const response = await updateUserPortfolio(updatedUserPotfolio);
      if (response === 'aborted' || !response?.data) {
        throw new Error('Portfolio save did not return portfolio data');
      }

      try {
        const portfolio = await getUserPortfolio(true, true, true);
        if (portfolio !== 'aborted' && portfolio.data) {
          setUserPortfolio(portfolio.data);
          return;
        }
      } catch (error) {
        console.error('Failed to refresh portfolio after saving it:', error);
      }
      setUserPortfolio((currentPortfolio) => ({
        ...currentPortfolio,
        ...updatedUserPotfolio,
      }));
    } catch (error) {
      console.error('Error saving portfolio:', error);
      throw new Error('Failed to save portfolio');
    }
  };

  return (
    <PlanContext.Provider
      value={{
        plan,
        setPlan: (newPlan) => setPlan(copyPlan(newPlan)),
        savePlan: handleSavePlan,
        updatedPlan,
        setUpdatedPlan: (newUpdatedPlan) => setUpdatedPlan(copyPlan(newUpdatedPlan)),
        userPortfolio,
        setUserPortfolio,
        saveUserPortfolio: handleSaveUserPortfolio,
        fundFamilies,
        wordpressCoreFundData,
        wordpressFocusedFundData,
        planningConfig,
        userPPR,
        isError,
        loading,
        planningButtonLoading,
        setPlanningButtonLoading,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
};

PlanProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
